export type VocabEntry = {
  english: string;
  german: string;
  letter: string;
  abbreviation: string | null;
  /** 1-2 short aviation-English sentences using the term in context (v0.4.0+). */
  examples?: string[];
};

export type ProgressState = "fresh" | "in_progress" | "wrong" | "mastered";

/**
 * Lernrichtung einer Progress-Row (Issue #95, Phase 2).
 *   - `en_de` = englisches Wort als Prompt, deutsche Übersetzung wählen (Bestandsverhalten)
 *   - `de_en` = deutsche Übersetzung als Prompt, englisches Wort wählen
 */
export type Direction = "en_de" | "de_en";

export const DIRECTIONS: readonly Direction[] = ["en_de", "de_en"];

/**
 * Aktive User-Wahl im Settings-Sheet (Issue #95, Phase 3 UI).
 * `mixed` = der Scheduler wechselt pro Frage zufällig zwischen den zwei Richtungen.
 */
export type LearningDirection = Direction | "mixed";

export const LEARNING_DIRECTIONS: readonly LearningDirection[] = ["en_de", "de_en", "mixed"];

/**
 * Lern-Modus (Issue #123).
 *   - `all`    = ganzer Vokabel-Pool, Bestandsverhalten, zählt für Zertifikat.
 *   - `marked` = nur Wörter aus der vom User gepflegten Markiert-Liste.
 *                Reine Trainingsumgebung — Antworten propagieren NICHT in
 *                progress.remaining_correct etc. (Spec 50 §6 Cert-Isolation).
 */
export type LearningMode = "all" | "marked";

export const LEARNING_MODES: readonly LearningMode[] = ["all", "marked"];

export type ProgressRecord = {
  english: string;
  direction: Direction;
  remainingCorrect: number;
  state: ProgressState;
  totalAsked: number;
  totalCorrect: number;
  totalWrong: number;
  lastSeenAt: number;
  lastAnsweredCorrect: boolean | null;
  consecutiveCorrect: number;
  earliestQuestionIndex: number;
};

export type SessionMeta = {
  key: "session";
  questionsAnswered: number;
  startedAt: number;
  totalElapsedMs: number;
};

export type ThemePref = "dark" | "light" | "system";

export type SettingsMeta = {
  key: "settings";
  rngSeed: number;
  reduceMotion: boolean;
  /** v0.3.0+. Older records may not have these — readers must default. */
  theme?: ThemePref;
  sound?: boolean;
  /** v0.11.0+ (Issue #95). Default 'en_de' für Bestandsverhalten. */
  learningDirection?: LearningDirection;
  /** v0.12.0+ (Issue #123). Default 'all' = ganzer Vokabel-Pool. */
  learningMode?: LearningMode;
};

/**
 * Marker-Eintrag (Issue #123). Schlanker Record — englishes Wort als PK plus
 * Zeitstempel fürs spätere Sortieren in der Listen-Übersicht. Server-Stand
 * ist der kanonische — beim Hydrate wird IDB aus der GET /me/progress
 * `markers`-Liste neu aufgebaut.
 */
export type MarkerRecord = {
  english: string;
  createdAt: number;
};

/**
 * Cached vocab list keyed by the server's ETag, populated on app boot via
 * `initVocab()` (issue #73). The cached blob survives offline boots and
 * gets revalidated against the server on every online boot.
 */
export type VocabCacheMeta = {
  key: "vocab-cache";
  etag: string;
  data: VocabEntry[];
  cachedAt: number;
};

export type MetaRecord = SessionMeta | SettingsMeta | VocabCacheMeta;

/**
 * Outbox entries queue mutations that need to reach the server. Phase 3 only
 * defines the schema; Phase 4 wires it into the login + online/offline flow.
 */
export type OutboxKind = "answer" | "reset" | "settings" | "marker";

export type OutboxRecord = {
  id?: number;
  kind: OutboxKind;
  payload: unknown;
  ts: number;
  status: "pending" | "in_flight" | "failed";
  retries: number;
  lastError?: string;
};
